import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {SequenceTopology} from 'constants/SequenceConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const topologyToDisplayText = {
  [SequenceTopology.CIRCULAR]: 'Circular',
  [SequenceTopology.LINEAR]: 'Linear',
};

interface TopologyDropdownProps {
  label: string;
  topology: SequenceTopology;
  disabled?: boolean;
  disabledTooltipText?: string;
  onChange: (sequenceTopology: SequenceTopology) => void;
}

/**
 * A dropdown to select whether a sequence or construct should have circular or linear topology.
 *
 * @param {TopologyDropdownProps} props Component props
 * @param {string} props.label Text to appear above the dropdown
 * @param {SequenceTopology} props.topology Which topology the dropdown has selected
 * @param {boolean} props.[disabled] Whether the dropdown should be disabled
 * @param {string} props.[disabledTooltipText] Text to show on an info icon tooltip if the dropdown is disabled
 * @param {function} props.onChange A function to be called when the dropdown value changes
 * @constructor
 */
export function TopologyDropdown(props: TopologyDropdownProps): JSX.Element {
  return (
    <FormGroup>
      <FieldLabel label={props.label} />
      {props.disabled && props.disabledTooltipText && (
        <Tooltip text={props.disabledTooltipText}>
          <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
        </Tooltip>
      )}
      <SelectDropdown
        displayName={topologyToDisplayText[props.topology]}
        disabled={props.disabled}
        menu={
          <Menu value={props.topology} width={MenuWidth.FULL}>
            {Object.values(SequenceTopology).map((topologyType) => (
              <Option
                key={topologyType}
                value={topologyType}
                onSelect={() => {
                  props.onChange(topologyType);
                }}
              >
                {topologyToDisplayText[topologyType]}
              </Option>
            ))}
          </Menu>
        }
        dataTestAttrs={{
          component: 'TopologyDropdown',
          element: 'selectDropdown',
        }}
      />
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  infoIcon: {
    marginLeft: '8px',
    color: GRAY_DARK,
  },
});
